// Configuration constants
export const TAG_DISPLAY_CONSTANTS = {
  BADGE_GAP: 8, // Gap between badges in pixels (matches Flex gap="8")
  MIN_TRUNCATED_TAG_WIDTH: 50, // Minimum width for readable truncated tag (px)
  COUNTER_BADGE_FALLBACK_WIDTH: 40, // Default counter badge width if not measured (px)
  COUNTER_BADGE_PADDING: 8, // Extra padding for counter badge to prevent clipping (px)
} as const;

export type TagDisplayState = {
  visibleCount: number;
  lastTruncated: boolean;
};

export type CalculateTagDisplayParams = {
  containerWidth: number;
  tags: Array<{ name: string }>;
  quantity?: number;
  tagRefs: Array<HTMLElement | null>;
  counterRef: HTMLElement | null;
};

/**
 * Calculates how many tags can fit in the available space and whether the last tag should be truncated
 */
export function calculateTagDisplay({
  containerWidth,
  tags,
  quantity,
  tagRefs,
  counterRef,
}: CalculateTagDisplayParams): TagDisplayState {
  const {
    BADGE_GAP,
    MIN_TRUNCATED_TAG_WIDTH,
    COUNTER_BADGE_FALLBACK_WIDTH,
    COUNTER_BADGE_PADDING,
  } = TAG_DISPLAY_CONSTANTS;

  // Measure the actual counter badge width, add padding for safety
  const counterBadgeWidth =
    (counterRef?.offsetWidth || COUNTER_BADGE_FALLBACK_WIDTH) + COUNTER_BADGE_PADDING;

  let totalWidth = 0;
  let visibleCount = 0;
  let lastTruncated = false;

  // If quantity is undefined, fit as many tags as possible in available space
  // Otherwise, respect the quantity limit
  const maxToConsider = quantity !== undefined ? Math.min(quantity, tags.length) : tags.length;

  // Always reserve space for counter initially (we'll adjust if all tags fit)
  // This ensures we don't over-allocate space to tags
  const availableWidthForTags = containerWidth - counterBadgeWidth - BADGE_GAP;

  // First pass: fit as many tags as possible
  for (let i = 0; i < maxToConsider; i++) {
    const tagWidth = tagRefs[i]?.offsetWidth || 0;
    if (tagWidth === 0) continue;

    const widthWithGap = totalWidth + tagWidth + (i > 0 ? BADGE_GAP : 0);

    // Tag fits completely
    if (widthWithGap <= availableWidthForTags) {
      totalWidth = widthWithGap;
      visibleCount = i + 1;
      continue;
    }

    // This tag doesn't fit at full width - check if we can fit it truncated
    const remainingWidth = availableWidthForTags - totalWidth - (i > 0 ? BADGE_GAP : 0);

    if (remainingWidth >= MIN_TRUNCATED_TAG_WIDTH) {
      visibleCount = i + 1;
      lastTruncated = true;
    }
    break;
  }

  // Second pass: try to add one more tag if it's the last one (avoiding +1 counter)
  const shouldTrySecondPass =
    visibleCount === maxToConsider && !lastTruncated && tags.length > visibleCount;

  if (!shouldTrySecondPass) {
    return { visibleCount, lastTruncated };
  }

  const nextTagIndex = visibleCount;
  const nextTagWidth = tagRefs[nextTagIndex]?.offsetWidth || 0;

  if (nextTagWidth === 0) {
    return { visibleCount, lastTruncated };
  }

  const remainingAfterNext = tags.length - visibleCount - 1;

  // ONLY add the next tag if it's the last one (avoiding +1)
  if (remainingAfterNext !== 0) {
    return { visibleCount, lastTruncated };
  }

  const spaceWithoutCounter = containerWidth - totalWidth - BADGE_GAP;

  // Try to fit the next tag at full width
  if (nextTagWidth + BADGE_GAP <= spaceWithoutCounter) {
    visibleCount += 1;
    return { visibleCount, lastTruncated };
  }

  // Try to fit it truncated
  if (spaceWithoutCounter >= MIN_TRUNCATED_TAG_WIDTH) {
    visibleCount += 1;
    lastTruncated = true;
  }

  return { visibleCount, lastTruncated };
}

/**
 * Calculates how many tags to render for measurement purposes
 */
export function getTagsToMeasure(quantity: number | undefined, totalTags: number): number {
  // If quantity is undefined, render all tags for dynamic fitting
  // Otherwise, render quantity + 1 for potential overflow detection
  return quantity !== undefined ? Math.min(quantity + 1, totalTags) : totalTags;
}
